import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { format } from "date-fns";
import { Loader2, Send } from "lucide-react";
import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { requireAuth } from "@/middleware/auth";
import { useSendLogQuery } from "@/queries/send";

export const Route = createFileRoute("/send/")({
	beforeLoad: async () => {
		await requireAuth();
	},
	component: RouteComponent,
});

function RouteComponent() {
	const queryOptions = useSendLogQuery();
	const { data, isLoading, error } = useQuery(queryOptions);

	const [search, setSearch] = useState<string>("");

	const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearch(e.target.value);
	};

	const filteredData = useMemo(() => {
		if (!data) return [];

		return data.filter((log) => {
			const searchTerm = search.toLowerCase();
			return (
				log.name.toLowerCase().includes(searchTerm) ||
				log.phone.includes(searchTerm) ||
				format(new Date(log.sent_at), "yyyy-MM-dd HH:mm:ss").includes(
					searchTerm,
				)
			);
		});
	}, [data, search]);

	if (isLoading) {
		return (
			<div className="flex justify-center items-center h-full">
				<div className="flex flex-col items-center gap-4">
					<Loader2 className="size-12 animate-spin text-blue-500" />
					<p className="text-lg text-gray-600 font-medium">
						전송 로그를 불러오는 중...
					</p>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex justify-center items-center h-full bg-gray-50">
				<div className="flex flex-col items-center gap-4">
					<div className="size-16 bg-red-100 rounded-full flex items-center justify-center">
						<Send className="size-8 text-red-500" />
					</div>
					<p className="text-lg text-gray-600 font-medium">
						전송 로그를 불러올 수 없습니다.
					</p>
					<Button onClick={() => window.location.reload()}>다시 시도</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="flex-1 container mx-auto px-4">
			<div className="flex items-center justify-end py-4 ml-3">
				<Input
					className="w-4/5 sm:w-1/3"
					placeholder="설명서 이름, 전화번호, 전송일시로 검색해주세요."
					value={search}
					onChange={handleSearch}
				/>
			</div>
			{filteredData && filteredData.length > 0 ? (
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>설명서 이름</TableHead>
							<TableHead>전화번호</TableHead>
							<TableHead className="w-32">전송일시</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{filteredData.map((log) => (
							<TableRow key={log.code}>
								<TableCell className="font-medium">{log.name}</TableCell>
								<TableCell>{log.phone}</TableCell>
								<TableCell>
									{format(new Date(log.sent_at), "yyyy-MM-dd HH:mm:ss")}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			) : (
				<div className="flex flex-col items-center justify-center py-20">
					<div className="size-24 bg-gray-50 rounded-full flex items-center justify-center mb-6">
						<Send className="size-12 text-gray-400" />
					</div>
					<h3 className="text-2xl font-bold text-gray-900 mb-2">
						{search ? "검색 결과가 없습니다" : "아직 전송 로그가 없습니다"}
					</h3>
					<p className="text-gray-600 text-center max-w-md">
						{search
							? "검색어를 다시 확인해주세요."
							: "첫 번째 전송 로그가 여기에 표시됩니다."}
					</p>
				</div>
			)}
		</div>
	);
}
